import type {
  CourseDetail,
  CourseSummary,
  DepartmentRecord,
  LegacyReview,
  ProfessorLookupEntry,
  ProfessorSummary,
  ReviewMedian,
  SiteSummary,
} from "./types";
import { termSortValue } from "./terms";

export interface PendingPublishedReview {
  id: string;
  courseId: string;
  professor: string;
  term: string;
  median?: ReviewMedian | null;
  comments: string;
  createdAt: string;
}

const REVIEW_MEDIAN_VALUES: ReviewMedian[] = ["A", "A-", "B+", "B", "B-", "C+", "Unsure"];
const GRADED_MEDIAN_VALUES = ["A", "A-", "B+", "B", "B-", "C+"] as const;
const REVIEW_MEDIAN_CAPTURE = "(A-|B\\+|B-|C\\+|A|B)";
const REVIEW_MEDIAN_PATTERNS = [
  new RegExp(`\\bmedian\\s*[:=]\\s*${REVIEW_MEDIAN_CAPTURE}(?=$|[^A-Z0-9])`, "gi"),
  new RegExp(`\\bmedian(?:\\s+grade)?\\s+(?:was|is|of|around)\\s+(?:an?\\s+)?${REVIEW_MEDIAN_CAPTURE}(?=$|[^A-Z0-9])`, "gi"),
];

function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function normalizeProfessorName(value: string): string {
  return normalizeWhitespace(value).toLowerCase();
}

function buildOrderedProfessorNames(course: CourseDetail, professors: ProfessorSummary[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  const offeringsByRecency = [...course.officialOfferings].sort(
    (left, right) => termSortValue(right.term) - termSortValue(left.term),
  );

  for (const offering of offeringsByRecency) {
    for (const instructor of offering.instructors) {
      const normalized = normalizeProfessorName(instructor);
      if (!normalized || seen.has(normalized)) {
        continue;
      }
      seen.add(normalized);
      ordered.push(instructor);
    }
  }

  for (const professor of professors) {
    const normalized = normalizeProfessorName(professor.name);
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    ordered.push(professor.name);
  }

  return ordered;
}

export function normalizeReviewMedian(value: string | null | undefined): ReviewMedian | null {
  const normalized = normalizeWhitespace(value ?? "").toUpperCase();
  if (!normalized) {
    return null;
  }

  if (normalized === "IDK") {
    return "Unsure";
  }

  const matched = REVIEW_MEDIAN_VALUES.find((candidate) => candidate.toUpperCase() === normalized);
  return matched ?? null;
}

export function inferReviewMedianFromComments(comments: string): ReviewMedian | null {
  const matches = new Set<ReviewMedian>();
  const normalizedComments = normalizeWhitespace(comments);

  for (const pattern of REVIEW_MEDIAN_PATTERNS) {
    const found = normalizedComments.matchAll(pattern);
    for (const match of found) {
      const median = normalizeReviewMedian(match[1]);
      if (median) {
        matches.add(median);
      }
    }
  }

  return matches.size === 1 ? [...matches][0] : null;
}

export function withBackfilledReviewMedian(review: LegacyReview): LegacyReview {
  return {
    ...review,
    median: normalizeReviewMedian(review.median) ?? inferReviewMedianFromComments(review.comments),
  };
}

function sortProfessorSummaries(professors: ProfessorSummary[]): ProfessorSummary[] {
  return [...professors].sort((left, right) => {
    if (right.reviewCount !== left.reviewCount) {
      return right.reviewCount - left.reviewCount;
    }
    return left.name.localeCompare(right.name);
  });
}

export function buildProfessorSummariesFromReviews(reviews: LegacyReview[]): ProfessorSummary[] {
  const counts = new Map<string, number>();

  for (const review of reviews) {
    const professor = normalizeWhitespace(review.professor);
    if (!professor) {
      continue;
    }
    counts.set(professor, (counts.get(professor) ?? 0) + 1);
  }

  return sortProfessorSummaries(
    [...counts.entries()].map(([name, reviewCount]) => ({ name, reviewCount })),
  );
}

export function attachProfessorSlugsToCourse(
  course: CourseDetail,
  professorLookupEntries: ReadonlyMap<string, string> | ProfessorLookupEntry[],
): CourseDetail {
  const professorSlugByNormalizedName = professorLookupEntries instanceof Map
    ? professorLookupEntries
    : new Map(
      Array.isArray(professorLookupEntries)
        ? professorLookupEntries.map((entry) => [entry.normalizedName, entry.slug] as const)
        : [],
    );

  let changed = false;
  const professors = course.professors.map((professor) => {
    const slug = professorSlugByNormalizedName.get(normalizeProfessorName(professor.name));
    if (!slug || professor.slug === slug) {
      return professor;
    }

    changed = true;
    return {
      ...professor,
      slug,
    };
  });

  return changed
    ? {
      ...course,
      professors,
    }
    : course;
}

export function mergePendingReviewsIntoCourses(
  courses: CourseDetail[],
  pendingReviews: PendingPublishedReview[],
  generatedAt = new Date().toISOString(),
): {
  courses: CourseDetail[];
  publishedReviewIds: string[];
  skippedReviewIds: string[];
} {
  const nextCourses = new Map(courses.map((course) => [course.courseId, { ...course, legacyReviews: [...course.legacyReviews] }]));
  const publishedReviewIds: string[] = [];
  const skippedReviewIds: string[] = [];
  const touchedCourseIds = new Set<string>();

  for (const review of pendingReviews) {
    const course = nextCourses.get(review.courseId);
    if (!course) {
      skippedReviewIds.push(review.id);
      continue;
    }

    if (course.legacyReviews.some((existing) => existing.id === review.id)) {
      skippedReviewIds.push(review.id);
      continue;
    }

    course.legacyReviews.push({
      id: review.id,
      professor: normalizeWhitespace(review.professor),
      term: review.term.toUpperCase(),
      median: normalizeReviewMedian(review.median),
      comments: normalizeWhitespace(review.comments),
      source: "legacy",
      createdAt: review.createdAt,
    });
    publishedReviewIds.push(review.id);
    touchedCourseIds.add(course.courseId);
  }

  for (const courseId of touchedCourseIds) {
    const course = nextCourses.get(courseId);
    if (!course) {
      continue;
    }
    nextCourses.set(courseId, applyCourseReviewDerivedFields(course, generatedAt));
  }

  return {
    courses: sortCoursesForStaticArtifacts([...nextCourses.values()]),
    publishedReviewIds,
    skippedReviewIds,
  };
}

export function sortCoursesForStaticArtifacts(courses: CourseDetail[]): CourseDetail[] {
  return [...courses].sort((left, right) => {
    if (right.legacyReviewCount !== left.legacyReviewCount) {
      return right.legacyReviewCount - left.legacyReviewCount;
    }
    if (left.department !== right.department) {
      return left.department.localeCompare(right.department);
    }
    return left.shortName.localeCompare(right.shortName);
  });
}

export function buildDepartmentsFromCourses(courses: CourseDetail[]): DepartmentRecord[] {
  const departments = new Map<string, DepartmentRecord>();

  for (const course of courses) {
    const current = departments.get(course.department) ?? {
      code: course.department,
      name: course.departmentName || course.department,
      courseCount: 0,
    };
    current.name = current.name || course.departmentName || course.department;
    current.courseCount += 1;
    departments.set(course.department, current);
  }

  return [...departments.values()].sort((left, right) => {
    if (right.courseCount !== left.courseCount) {
      return right.courseCount - left.courseCount;
    }
    return left.code.localeCompare(right.code);
  });
}

export function buildSiteSummaryFromCourses(
  courses: CourseDetail[],
  departments: DepartmentRecord[],
  generatedAt = new Date().toISOString(),
): SiteSummary {
  return {
    courseCount: courses.length,
    departmentCount: departments.length,
    legacyReviewCount: courses.reduce((total, course) => total + course.legacyReviewCount, 0),
    importedLegacyReviewCount: courses.reduce((total, course) => total + course.legacyReviews.length, 0),
    generatedAt,
  };
}

export function toCourseSummarySnapshot(course: CourseDetail): CourseSummary {
  return {
    courseId: course.courseId,
    courseKey: course.courseKey,
    legacyCourseId: course.legacyCourseId,
    slug: course.slug,
    shortName: course.shortName,
    title: course.title,
    department: course.department,
    departmentName: course.departmentName,
    orcUrl: course.orcUrl,
    description: course.description,
    distribs: course.distribs,
    offeredLabel: course.offeredLabel,
    offeredPeriods: course.offeredPeriods,
    legacyReviewCount: course.legacyReviewCount,
    typicalMedian: course.typicalMedian ?? null,
    typicalMedianReviewCount: course.typicalMedianReviewCount ?? 0,
    professorCount: course.professorCount,
    professorNames: course.professorNames,
    officialOfferings: course.officialOfferings,
  };
}

export function sortLegacyReviewsByMostRecent(reviews: LegacyReview[]): LegacyReview[] {
  return [...reviews].sort((left, right) => {
    const termDelta = termSortValue(right.term) - termSortValue(left.term);
    if (termDelta !== 0) {
      return termDelta;
    }

    const leftDate = left.createdAt ? new Date(left.createdAt).getTime() : 0;
    const rightDate = right.createdAt ? new Date(right.createdAt).getTime() : 0;
    if (rightDate !== leftDate) {
      return rightDate - leftDate;
    }

    return left.id.localeCompare(right.id);
  });
}

export function dedupeLegacyReviewsByIdOrContent(reviews: LegacyReview[]): LegacyReview[] {
  const seenIds = new Set<string>();
  const seenFallbackKeys = new Set<string>();
  const deduped: LegacyReview[] = [];

  for (const review of reviews) {
    const reviewId = normalizeWhitespace(review.id);
    if (reviewId) {
      if (seenIds.has(reviewId)) {
        continue;
      }
      seenIds.add(reviewId);
      deduped.push(review);
      continue;
    }

    const fallbackKey = [
      normalizeWhitespace(review.term).toUpperCase(),
      normalizeWhitespace(review.professor),
      normalizeWhitespace(review.comments),
    ].join("|");
    if (seenFallbackKeys.has(fallbackKey)) {
      continue;
    }
    seenFallbackKeys.add(fallbackKey);
    deduped.push(review);
  }

  return deduped;
}

function currentDartmouthTermValue(referenceDateInput: string | Date): number {
  const referenceDate = referenceDateInput instanceof Date ? referenceDateInput : new Date(referenceDateInput);
  if (Number.isNaN(referenceDate.getTime())) {
    return currentDartmouthTermValue(new Date());
  }

  const year = referenceDate.getUTCFullYear() % 100;
  const month = referenceDate.getUTCMonth();
  const season = month <= 2
    ? "W"
    : month <= 5
      ? "S"
      : month <= 7
        ? "X"
        : "F";

  return termSortValue(`${String(year).padStart(2, "0")}${season}`);
}

export function computeTypicalMedianFromReviews(
  reviews: LegacyReview[],
  referenceDate = new Date().toISOString(),
): {
  typicalMedian: string | null;
  reviewCount: number;
} {
  const currentTermValue = currentDartmouthTermValue(referenceDate);
  const cutoffTermValue = currentTermValue - 50;

  const eligibleMedianIndexes = reviews
    .map((review) => ({
      termValue: termSortValue(review.term),
      median: normalizeReviewMedian(review.median),
    }))
    .filter((review): review is { termValue: number; median: (typeof GRADED_MEDIAN_VALUES)[number] } =>
      review.termValue >= cutoffTermValue
      && review.median !== null
      && review.median !== "Unsure",
    )
    .map((review) => GRADED_MEDIAN_VALUES.indexOf(review.median))
    .filter((medianIndex) => medianIndex >= 0)
    .sort((left, right) => left - right);

  if (eligibleMedianIndexes.length === 0) {
    return {
      typicalMedian: null,
      reviewCount: 0,
    };
  }

  const upperMiddleIndex = Math.floor(eligibleMedianIndexes.length / 2);
  if (eligibleMedianIndexes.length % 2 === 1) {
    return {
      typicalMedian: GRADED_MEDIAN_VALUES[eligibleMedianIndexes[upperMiddleIndex]],
      reviewCount: eligibleMedianIndexes.length,
    };
  }

  const leftMedianIndex = eligibleMedianIndexes[upperMiddleIndex - 1];
  const rightMedianIndex = eligibleMedianIndexes[upperMiddleIndex];
  return {
    typicalMedian: leftMedianIndex === rightMedianIndex
      ? GRADED_MEDIAN_VALUES[leftMedianIndex]
      : `${GRADED_MEDIAN_VALUES[leftMedianIndex]}/${GRADED_MEDIAN_VALUES[rightMedianIndex]}`,
    reviewCount: eligibleMedianIndexes.length,
  };
}

export function applyCourseReviewDerivedFields(
  course: CourseDetail,
  generatedAt = course.generatedAt ?? new Date().toISOString(),
): CourseDetail {
  const legacyReviews = dedupeLegacyReviewsByIdOrContent(
    sortLegacyReviewsByMostRecent(course.legacyReviews),
  );
  const professors = buildProfessorSummariesFromReviews(legacyReviews);
  const professorNames = buildOrderedProfessorNames(course, professors);
  const { typicalMedian, reviewCount } = computeTypicalMedianFromReviews(legacyReviews, generatedAt);
  const legacyReviewCount = legacyReviews.length;

  return {
    ...course,
    legacyReviews,
    legacyReviewCount,
    typicalMedian,
    typicalMedianReviewCount: reviewCount,
    professors,
    professorCount: professorNames.length,
    professorNames,
    legacyReviewAccess: legacyReviewCount > 0
      ? "imported"
      : course.legacyReviewAccess === "restricted"
        ? "restricted"
        : "none",
    generatedAt,
  };
}
